import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useOrderData } from 'hooks/orderForms/useOrderData';
import { useOrderedProducts } from 'hooks/orderForms/useOrderedProducts';
import { useOrderForm } from 'services/repositories/orderForms/orderForms';
import { setProductsHavePrice, useOrderFormStore } from 'stores/orderForm/orderForm';
import { calculateTotalTax, createProductsRecord, isOrderFormWithPrices } from 'support/helpers/orderForms/orderForms';
import { RouteParams } from 'support/helpers/types/types';
import { OrderFormsRoutes } from 'support/types';
import { calculateSubtotal } from 'pages/OrderForm/hooks/useOrderFormCalculations';
import { shouldPayTaxes } from 'pages/OrderFormCheckout/utils/shouldPayTaxes';
import { LoadingPage } from 'components/Loading/LoadingPage';

import { Divider } from './components/Divider';
import { Extras } from './components/Extras';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { OrderedProducts } from './components/OrderedProducts/OrderedProducts';
import { OrderTaxes } from './components/OrderTaxes';
import { Subtotal } from './components/Subtotal';
import { Total } from './components/Total';

export function OrderBreakdown() {
  const { productsHavePrice } = useOrderFormStore((state) => ({
    productsHavePrice: state.productsHavePrice,
  }));
  const { supplierSlug } = useParams<RouteParams<OrderFormsRoutes.root>>();
  const { data: orderFormData, isLoading: isLoadingOrderForm } = useOrderForm({
    variables: { supplierSlugName: supplierSlug },
  });

  const [orderData] = useOrderData(supplierSlug as string);
  const orderedProducts = useOrderedProducts(supplierSlug as string);
  const productDataById = useMemo(() => createProductsRecord(orderFormData?.data), [orderFormData]);

  useEffect(() => {
    setProductsHavePrice(isOrderFormWithPrices(orderedProducts, productDataById));
  }, [orderedProducts, productDataById]);

  if (isLoadingOrderForm) {
    return <LoadingPage />;
  }

  const currency = orderFormData?.data?.currency;
  const subtotal = calculateSubtotal(orderedProducts, productDataById);
  const total = subtotal + calculateTotalTax({ orderedProducts, productsData: productDataById });

  return (
    <div className="flex w-full flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white">
      <div className="px-8 pb-6 pt-10">
        {productsHavePrice ? (
          <>
            <Header amount={total} currency={currency} />
            <Divider />
          </>
        ) : null}
        <OrderedProducts orderedProducts={orderedProducts} currency={currency} productsData={productDataById} />

        {productsHavePrice ? (
          <>
            <Divider />
            <Extras>
              <Subtotal amount={subtotal} currency={currency} />
              {shouldPayTaxes(orderData.parties) && (
                <OrderTaxes currency={currency} orderedProducts={orderedProducts} productsData={productDataById} />
              )}
            </Extras>
            <Divider />
            <Total amount={total} currency={currency} />
          </>
        ) : null}
      </div>
      <Footer />
    </div>
  );
}
